/*  HuddlePanel — Producer / master's "let costume + action + camera
 *  huddle for 2 hours" toggle.
 *
 *  Two surfaces:
 *    · A compact banner on the project cubicle page that lists any
 *      currently-active huddles (with a live "ends in Xh Ym" counter
 *      and an End button for the creator).
 *    · A modal (opened via a top-level "Call Huddle" button) where
 *      the producer picks 2-6 cubicles, a duration (30 · 60 · 120 ·
 *      240 · 480 · 720 minutes), and an optional reason.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Handshake, X, Clock, Users, Plus, ArrowRight, PowerOff, AlertTriangle,
} from "lucide-react";


const DURATIONS = [
  { min: 30,  label: "30 min" },
  { min: 60,  label: "1 h"    },
  { min: 120, label: "2 h"    },
  { min: 240, label: "4 h"    },
  { min: 480, label: "8 h"    },
  { min: 720, label: "12 h"   },
];


function _fmtRemaining(endsAtIso) {
  if (!endsAtIso) return "";
  const ms = new Date(endsAtIso).getTime() - Date.now();
  if (ms <= 0) return "ended";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m left`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m left` : `${h}h left`;
}


export default function HuddlePanel({ projectId, canManage, availableCubicles }) {
  const [huddles, setHuddles] = useState([]);
  const [open, setOpen]       = useState(false);
  const [busy, setBusy]       = useState(false);
  const [tick, setTick]       = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/huddles`,
        { params: { only_active: true } });
      setHuddles(data.items || []);
    } catch (e) { /* silent */ }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  // Live-tick the remaining-time chips every 30s.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const end = async (hid) => {
    if (!window.confirm("End this huddle now? Cross-cubicle access ends immediately.")) return;
    try {
      await api.post(`/projects/${projectId}/huddles/${hid}/end`);
      toast.success("Huddle ended · access reverted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="huddle-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mt-6">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-white/45">
          <Handshake size={12}/> Cross-Dept Huddles · <span className="text-white/70">{huddles.length} active</span>
        </div>
        {canManage && (
          <Button onClick={() => setOpen(true)}
                  className="btn-themed h-9 px-4 rounded-full uppercase tracking-[0.22em] text-[10px]"
                  data-testid="huddle-open-modal">
            <Plus size={12}/> Call huddle
          </Button>
        )}
      </div>

      {huddles.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="huddle-active-list">
          {huddles.map((h) => (
            <div key={h.id}
                 className="rounded-2xl border border-emerald-400/25 bg-emerald-500/[0.06] p-4 flex items-center gap-4 flex-wrap"
                 data-testid={`huddle-row-${h.id}`}>
              <div className="w-9 h-9 rounded-lg bg-emerald-400/15 flex items-center justify-center">
                <Handshake size={16} className="text-emerald-300"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.22em] uppercase text-emerald-300">
                  {h.title}
                </div>
                <div className="mt-1 text-sm text-white/85 flex items-center gap-1.5 flex-wrap">
                  {h.cubicles.map((c, i) => (
                    <span key={c} className="inline-flex items-center gap-1">
                      <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] uppercase tracking-wider">
                        {c.replace(/_/g, " ")}
                      </span>
                      {i < h.cubicles.length - 1 && <ArrowRight size={10} className="text-white/40"/>}
                    </span>
                  ))}
                </div>
                {h.reason && (
                  <div className="mt-1 text-[11px] text-white/50 italic">
                    &ldquo;{h.reason}&rdquo;
                  </div>
                )}
              </div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/60 tabular-nums flex items-center gap-1"
                   data-testid={`huddle-remaining-${h.id}`}>
                <Clock size={10}/> {_fmtRemaining(h.ends_at)}
              </div>
              {canManage && (
                <Button variant="ghost"
                        onClick={() => end(h.id)}
                        className="rounded-full text-red-300 hover:text-red-100 h-8 px-3 uppercase tracking-[0.22em] text-[10px]"
                        data-testid={`huddle-end-${h.id}`}>
                  <PowerOff size={11}/> End
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {open && (
        <HuddleModal
          projectId={projectId}
          availableCubicles={availableCubicles}
          onClose={() => setOpen(false)}
          onCreated={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}


function HuddleModal({ projectId, availableCubicles, onClose, onCreated }) {
  const [picked, setPicked] = useState(new Set());
  const [duration, setDuration] = useState(120);
  const [title, setTitle] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const cubicles = useMemo(() => availableCubicles || [], [availableCubicles]);

  const toggle = (key) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else if (next.size < 6) next.add(key);
      else toast.error("A huddle can include up to 6 cubicles");
      return next;
    });
  };

  const submit = async () => {
    if (picked.size < 2) {
      toast.error("Pick at least 2 cubicles to huddle");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/huddles`, {
        cubicles:         [...picked],
        duration_minutes: duration,
        title:            title.trim(),
        reason:           reason.trim(),
      });
      toast.success(`Huddle called · ${picked.size} cubicles · ${DURATIONS.find(d => d.min === duration)?.label}`);
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to call huddle");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="huddle-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-neutral-950 border border-white/15 rounded-2xl p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-300">
            Call a Huddle
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"
                  data-testid="huddle-close-btn">
            <X size={16}/>
          </button>
        </div>
        <div className="mt-2 font-display text-2xl uppercase tracking-tight">
          Pull departments together for a scene
        </div>
        <p className="mt-1 text-xs text-white/55 leading-relaxed">
          Members of every cubicle you tick will get temporary access to
          each other&apos;s cubicles until the huddle ends. Perfect for a
          stunt scene where costume, action & camera need one shared room.
        </p>

        <div className="mt-5">
          <div className="text-[9px] tracking-[0.22em] uppercase text-white/50 mb-2">
            Cubicles · pick 2 to 6 ({picked.size} selected)
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2"
               data-testid="huddle-cubicle-grid">
            {cubicles.map((c) => {
              const on = picked.has(c.key);
              return (
                <button key={c.key} type="button"
                        onClick={() => toggle(c.key)}
                        className={"rounded-lg px-3 py-2 text-left transition text-[11px] tracking-[0.14em] uppercase "
                          + (on
                             ? "bg-emerald-500/15 border border-emerald-400/50 text-emerald-100"
                             : "bg-black/40 border border-white/10 text-white/70 hover:text-white")}
                        data-testid={`huddle-pick-${c.key}`}>
                  <div className="flex items-center gap-2">
                    {on && <span className="w-2 h-2 rounded-full bg-emerald-400"/>}
                    <span className="truncate">{c.label || c.key}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5">
          <div className="text-[9px] tracking-[0.22em] uppercase text-white/50 mb-2">
            Duration
          </div>
          <div className="flex flex-wrap gap-2" data-testid="huddle-duration-row">
            {DURATIONS.map((d) => (
              <button key={d.min}
                      type="button"
                      onClick={() => setDuration(d.min)}
                      className={"rounded-full px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase transition "
                        + (duration === d.min
                           ? "bg-white text-black"
                           : "border border-white/15 text-white/70 hover:text-white")}
                      data-testid={`huddle-duration-${d.min}`}>
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
              Title (optional)
            </label>
            <Input value={title}
                   onChange={(e) => setTitle(e.target.value)}
                   placeholder="Stunt scene · S4 Day 12"
                   className="bg-black/40 border-white/15 mt-1"
                   data-testid="huddle-title-input"/>
          </div>
          <div>
            <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
              Reason (optional)
            </label>
            <Input value={reason}
                   onChange={(e) => setReason(e.target.value)}
                   placeholder="Backup harness rig discussion"
                   className="bg-black/40 border-white/15 mt-1"
                   data-testid="huddle-reason-input"/>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose}
                  className="text-white/60"
                  data-testid="huddle-cancel-btn">Cancel</Button>
          <Button onClick={submit}
                  disabled={busy || picked.size < 2}
                  className="rounded-full h-10 px-6 uppercase tracking-[0.22em] text-[11px] bg-emerald-500 text-black hover:bg-emerald-400"
                  data-testid="huddle-submit-btn">
            <Handshake size={12}/> {busy ? "Calling…" : "Call huddle"}
          </Button>
        </div>
      </div>
    </div>
  );
}
